import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router'

import { ALL_GAMES } from '@hazel/shared'

import { KeyLegend } from '../components/KeyLegend.js'
import { GameCardFace, PICKER_GRID, PlainCardFace } from '../components/PickerCard.js'
import { useGameKeys } from '../games/useGameKeys.js'
import { usePickUpReleases } from '../layouts/usePickUpReleases.js'
import { useSound } from '../sound/sound-context.js'
import { gameAtKey, shortcutFor, shortcutLegend } from './gameShortcuts.js'

/**
 * Home. Driven entirely by the roster in packages/shared, so adding game five
 * is a data change rather than a layout change.
 *
 * Two ways in from the keys, because they suit different moments. Arrows and
 * Enter are for browsing — you can see where you are and change your mind.
 * The numbers are for somebody who already knows which game they want, which
 * after a fortnight is every time. Both matter on a television, where the
 * alternative is tabbing.
 */
/**
 * Everything this screen can open: the games, and then the room.
 *
 * One number rather than two, because the keys, the arrow bounds, End, and the
 * legend all have to agree about how many things there are — and four copies
 * of a count is how one of them comes to be wrong by one.
 */
const OPENABLE = ALL_GAMES.length + 1

export function GamePicker() {
  const [cursor, setCursor] = useState(0)

  /*
   * The one screen a release may be picked up from.
   *
   * The television is left open on this for hours at a time, and there is
   * nothing here to lose by reloading — unlike a round, which a reload would
   * abandon. See `usePickUpReleases` for why it is only ever called here.
   */
  usePickUpReleases()

  const sound = useSound()

  // The menu's own music, asked for once and left alone.
  useEffect(() => {
    sound.music('menu')
  }, [sound])

  /** The cards, so the cursor can carry real focus with it. */
  const links = useRef(new Map<number, HTMLAnchorElement>())

  function moveTo(next: number): void {
    // Clamped rather than wrapped, the same as the memory board: a cursor that
    // reappears at the other end loses anybody who was not watching it.
    //
    // Clamped rather than ignored, though, so the first arrow press always
    // lands somewhere. Before anything has focus an ignored keypress does
    // nothing at all, which from a sofa is indistinguishable from a dead
    // keyboard.
    const target = Math.min(Math.max(next, 0), OPENABLE - 1)

    setCursor(target)
    links.current.get(target)?.focus()
  }

  useGameKeys((event) => {
    // A key opens that game outright. Nothing is "selected" first, because
    // pressing 2 and landing on a highlighted card you then have to confirm is
    // two steps where she asked for one.
    //
    // Numbers for the first nine and letters after them — see
    // `gameShortcuts.ts` for why nine is where the numbers stop.
    const at = gameAtKey(event.key, OPENABLE)
    if (at !== null) {
      const chosen = links.current.get(at)
      if (chosen) {
        event.preventDefault()
        chosen.click()
      }
      return
    }

    switch (event.key) {
      case 'ArrowLeft':
      case 'ArrowUp':
        event.preventDefault()
        return moveTo(cursor - 1)
      case 'ArrowRight':
      case 'ArrowDown':
        event.preventDefault()
        return moveTo(cursor + 1)
      /*
       * The ends of the roster, not the ends of the scroll.
       *
       * Left alone, these are the frame's and they scroll the region without
       * moving the cursor — so the ring ends up on a card that is off the
       * screen, or half behind the header, and Enter opens something the
       * screen gives no sign of. Measured: End put the ring on a card at 72px
       * with the region starting at 113px, and Enter opened it.
       *
       * Claiming them here moves the cursor instead, and the cursor takes the
       * view with it because it carries real focus.
       */
      case 'Home':
        event.preventDefault()
        return moveTo(0)
      case 'End':
        event.preventDefault()
        return moveTo(OPENABLE - 1)
      default:
    }
  })

  return (
    <div className="flex flex-col gap-6">
      <h1 className="text-(length:--text-hero)">Choose a game</h1>

      {/*
        The grid runs past the screen, and that is the point rather than a
        thing to tune away: six games do not fit and nor will the seventh.
        There are six more parked in the backlog, so the roster is meant to
        outgrow one screen and the frame is what carries it (DOL-66). How many
        across at each size lives in `PICKER_GRID`, shared with the room.
      */}
      <ul className={PICKER_GRID}>
        {ALL_GAMES.map((game, index) => (
          <li key={game.key}>
            <Link
              to={`/play/${game.key}`}
              ref={(element) => {
                if (element) links.current.set(index, element)
                else links.current.delete(index)
              }}
              // One tab stop for the whole roster rather than one per game,
              // and Tab lands where the cursor already is.
              tabIndex={index === cursor ? 0 : -1}
              className="group block h-full no-underline"
              /*
                The key is in the name, so somebody who cannot see the keycap
                is still told which one opens this game — and a game far
                enough down the roster to have none says so rather than naming
                a key that does nothing.
              */
              aria-label={
                shortcutFor(index)
                  ? `Play ${game.title}. Press ${shortcutFor(index)}`
                  : `Play ${game.title}`
              }
              onFocus={() => setCursor(index)}
            >
              <GameCardFace game={game} shortcut={shortcutFor(index)} caption={game.blurb} />
            </Link>
          </li>
        ))}
        {/*
          The room, as the last entry rather than beside them.

          It is not a game, and it was tempting to give it a corner of its own
          with a letter for a key. But then the menu has two numbering rules,
          and the one used less often is the one that comes to disagree — this
          app has already shipped a legend offering a key that did nothing.
          Numbered from `shortcutFor` like every card, it is 8 today and 9 the
          day an eighth game lands, without anybody remembering to move it.

          Offered on every device. A tablet propped against the sugar bowl
          hosts perfectly well, and hiding it would need a rule about which
          screens are allowed to — which is the sort of rule that is later
          wrong on a device nobody tested.
        */}
        <li>
          <Link
            to="/host"
            ref={(element) => {
              if (element) links.current.set(ALL_GAMES.length, element)
              else links.current.delete(ALL_GAMES.length)
            }}
            tabIndex={ALL_GAMES.length === cursor ? 0 : -1}
            className="group block h-full no-underline"
            aria-label={
              shortcutFor(ALL_GAMES.length)
                ? `Play together on the telly. Press ${shortcutFor(ALL_GAMES.length)}`
                : 'Play together on the telly'
            }
            onFocus={() => setCursor(ALL_GAMES.length)}
          >
            <PlainCardFace shortcut={shortcutFor(ALL_GAMES.length)} title="Play together">
              Put a game on the telly. Everybody else joins from their own phone.
            </PlainCardFace>
          </Link>
        </li>
      </ul>

      <KeyLegend
        shortcuts={[
          // From the same two rules the badges are drawn from, so the legend
          // cannot come to promise a key the cards do not offer.
          ...shortcutLegend(OPENABLE),
          { keys: ['←', '→'], does: 'Look through them' },
          { keys: ['Enter'], does: 'Open the one you are on' },
        ]}
      />
    </div>
  )
}
